import useNav from '../hooks/useNav';
import { useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import * as Atom from '../recoil';
import {
  mortgageSelector,
  mtgSavePreReqTempSelector,
} from '../recoil/selector';
import { useBaseParam } from '../hooks/useBaseParam';
import { mutateSavePreRequestTemp } from '../../infra/api/mtg/mutateSavePreRequestTemp';
import { mutateSavePreRequest } from '../../infra/api/mtg/mutateSavePreRequest';

interface IMenuItem {
  text: string;
  value: string;
}
interface IMenuBottomsheet {
  open: boolean;
  title: string;
  subTitle?: string;
  onClose: () => void;
  onChange: () => void;
  items: IMenuItem[];
  defaultValue: any;
}
type NavigationType = 'POP' | 'PUSH' | 'REPLACE';
const initSheet = {
  open: false,
  title: '',
  onClose: () => {},
  onChange: () => {},
  items: [],
  defaultValue: {},
};

export const useMtgConditions = () => {
  // navigate
  const location = useLocation();
  const { navigate, redirectNavigate } = useNav();
  const navigateNextStep = (type: NavigationType, destination?: string) => {
    const pathname = location.pathname.slice(0, -1);
    const step = Number(location.pathname.slice(-1));
    if (type === 'PUSH') {
      return redirectNavigate(pathname + (step + 1), { replace: true });
    } else if (type === 'POP') {
      return redirectNavigate(pathname + (step - 1), { replace: true });
    } else if (type === 'REPLACE') {
      return navigate(destination ?? '', { replace: true });
    } else {
      return;
    }
  };

  // selectSheet
  const [selectSheet, setSelectSheet] = useState<any>(initSheet);
  const openSelectSheet = (codeGrp: string, list?: any) => {
    if (menuBottomsheetList[codeGrp]) {
      setSelectSheet(() => {
        return {
          open: true,
          onClose: closeSelectSheet,
          ...menuBottomsheetList[codeGrp],
        };
      });
    } else if (list) {
      setSelectSheet(() => {
        return {
          open: true,
          onClose: closeSelectSheet,
          stateNm: list.stateNm ?? '',
          title: list.title ?? '',
          subTitle: list.subTitle ?? '',
          items: list.items ?? [],
        };
      });
    } else {
      navigateNextStep('PUSH');
    }
  };
  const closeSelectSheet = () => {
    setSelectSheet(() => {
      return { ...initSheet };
    });
  };

  // onChange mortgageInfo
  const [mortgageInfo, setMortgageInfo] = useRecoilState(Atom.mortgageInfo);
  const [, setMortgageSelector] = useRecoilState(mortgageSelector);

  const relatedMap: any = {
    tenantYn: { key: 'tenantAmt', value: null },
    previousYn: { key: 'previousAmt', value: null },
  };

  const onChangeWrappingInput = (val: any) => {
    const relateValue = relatedMap[val.key];
    if (relateValue) {
      setMortgageSelector((prev: any) => {
        return {
          ...prev,
          key: relateValue.key,
          value: relateValue.value,
        };
      });
    }

    if (val.key === 'openDt') {
      val.value = val.value.replace(/[^0-9]/g, '');
    }

    setMortgageSelector((prev: any) => {
      return {
        ...prev,
        key: val.key,
        value: { text: val.text ?? '', value: val.value },
      };
    });
    if (val.text === '사업 자금') {
      // 대출 목적이 사업 자금이면
      setMortgageSelector((prev: any) => {
        return {
          ...prev,
          key: 'jobType',
          value: { text: '사업자', value: '04' }, // 직업을 사업자로 지정해준다.
        };
      });
    }
  };
  const onChangeInput = (val: any) => {
    setMortgageSelector((prev: any) => {
      return {
        key: val.key,
        value: val.value,
      };
    });
  };

  // codeList mapping
  const [codeList] = useRecoilState(Atom.commonCodes);
  const [mappedCodeList, setMappedCodeList] = useState<any>([]);
  const mtgCodeGrp = ['C001', 'C046', 'C047', 'C048', 'C049', 'C050'];
  const menuBottomsheetList: any = {
    C04910: {
      stateNm: 'firstYn',
      title: '생애 첫 주택 구입 유무',
      subTitle: '부부라면 두 분 모두 첫 구입이신지 알려주세요',
      items: mappedCodeList['C04910'],
    },
    C04920: {
      stateNm: 'disposalPlanYn',
      title: '주택 처분 계획 여부',
      subTitle: '',
      items: mappedCodeList['C04920'],
    },
    C046: {
      stateNm: 'houseType',
      title: '주택 유형',
      subTitle: '',
      items: mappedCodeList['C046']?.slice(0, -2), // todo 단독 주택, 다가구 주택 준비 중
    },
    C047: {
      stateNm: 'marriageCd',
      title: '결혼 여부',
      subTitle: '',
      items: mappedCodeList['C047'],
    },
    C001: {
      stateNm: 'jobType',
      title: '직업',
      subTitle: '',
      items: mappedCodeList['C001'],
    },
    C050: {
      stateNm: 'floor',
      title: '층수',
      subTitle: '',
      items: mappedCodeList['C050'],
    },
    Tenant10: {
      stateNm: 'previousYn',
      title: '대출금 여부',
      subTitle: '',
      items: mappedCodeList['Tenant10'],
    },
    Tenant20: {
      stateNm: 'tenantYn',
      title: '세입자 여부',
      subTitle: '',
      items: mappedCodeList['Tenant20'],
    },
    IA10: {
      stateNm: 'isApartment',
      title: '보유하신 주택이 아파트인가요?',
      items: mappedCodeList['IA10'],
    },
  };
  useEffect(() => {
    if (codeList) {
      setMappedCodeList(() => {
        const list1 = codeList
          .filter((codeItem: any) => mtgCodeGrp.includes(codeItem.codeGrp))
          .reduce((acc: any, cur: any) => {
            acc[cur.codeGrp] = cur.list.map((item: any) => {
              return {
                text: item.codeNm,
                value: item.code,
                desc: item.description,
              };
            });
            return acc;
          }, {});
        const list2 = {
          C04910: [
            {
              text: '첫 구매에요',
              value: 'Y',
              desc: '첫 구매라면 대출한도가 높아질 수 있어요',
            },
            {
              text: '첫 구매가 아니에요',
              value: 'N',
              desc: '',
            },
          ],
          C04920: [
            {
              text: '처분 계획 있어요',
              value: 'Y',
              desc: '처분 예정일 경우 대출한도가 높아질 수 있어요',
            },
            { text: '처분 계획 없어요', value: 'N' },
          ],
          Tenant10: [
            { text: '대출금이 있어요', value: 'Y' },
            { text: '대출금이 없어요', value: 'N' },
          ],
          Tenant20: [
            { text: '세입자가 있어요', value: 'Y' },
            { text: '세입자가 없어요', value: 'N' },
          ],
          IA10: [
            { text: '아파트를 보유하고 있어요', value: 'Y' },
            { text: '기타 주택을 보유하고 있어요', value: 'N' },
          ],
        };
        return { ...list1, ...list2 };
      });
    }
  }, []);

  const mtgSavePreReqTemp = useRecoilValue(mtgSavePreReqTempSelector);
  delete mtgSavePreReqTemp.isApartment;
  // saveInputInfoTemp
  const putInputInfoTemp = useBaseParam(
    mutateSavePreRequestTemp,
    {
      ...mtgSavePreReqTemp,
      hopefulAmt: mtgSavePreReqTemp.hopefulAmt
        ? mtgSavePreReqTemp.hopefulAmt * 10000
        : mtgSavePreReqTemp.hopefulAmt,
      previousAmt: mtgSavePreReqTemp.previousAmt
        ? mtgSavePreReqTemp.previousAmt * 10000
        : mtgSavePreReqTemp.previousAmt,
      tenantAmt: mtgSavePreReqTemp.tenantAmt
        ? mtgSavePreReqTemp.tenantAmt * 10000
        : mtgSavePreReqTemp.tenantAmt,
      salaryAmt: mtgSavePreReqTemp.salaryAmt
        ? mtgSavePreReqTemp.salaryAmt * 10000
        : mtgSavePreReqTemp.salaryAmt,
      houseAmt: mtgSavePreReqTemp.housePrice
        ? mtgSavePreReqTemp.housePrice * 10000
        : mtgSavePreReqTemp.housePrice,
      disposalPlanYn:
        mtgSavePreReqTemp.disposalPlanYn === ''
          ? null
          : mtgSavePreReqTemp.disposalPlanYn,
      previousYn:
        mtgSavePreReqTemp.previousYn === ''
          ? null
          : mtgSavePreReqTemp.previousYn,
      tenantYn:
        mtgSavePreReqTemp.tenantYn === '' ? null : mtgSavePreReqTemp.tenantYn,
      firstYn:
        mtgSavePreReqTemp.firstYn === '' ? null : mtgSavePreReqTemp.firstYn,
      openDt: mtgSavePreReqTemp.openDt === '' ? null : mtgSavePreReqTemp.openDt,
    },
    { noToast: true },
  );
  const saveInputInfoTemp = async () => {
    if (!mortgageInfo.purposeCd) {
      return;
    }

    const response = await putInputInfoTemp();
    if (!mortgageInfo.preRequestSeq) {
      setMortgageInfo((prev: any) => {
        return {
          ...prev,
          preRequestSeq: response.data.result.tempResult.preRequestSeq,
        };
      });
    }
  };

  // saveInputInfo
  const putInputInfo = useBaseParam(mutateSavePreRequest, {
    ...mtgSavePreReqTemp,

    hopefulAmt: mtgSavePreReqTemp.hopefulAmt
      ? mtgSavePreReqTemp.hopefulAmt * 10000
      : mtgSavePreReqTemp.hopefulAmt,
    previousAmt: mtgSavePreReqTemp.previousAmt
      ? mtgSavePreReqTemp.previousAmt * 10000
      : mtgSavePreReqTemp.previousAmt,
    tenantAmt: mtgSavePreReqTemp.tenantAmt
      ? mtgSavePreReqTemp.tenantAmt * 10000
      : mtgSavePreReqTemp.tenantAmt,
    salaryAmt: mtgSavePreReqTemp.salaryAmt
      ? mtgSavePreReqTemp.salaryAmt * 10000
      : mtgSavePreReqTemp.salaryAmt,
    houseAmt: mtgSavePreReqTemp.housePrice
      ? mtgSavePreReqTemp.housePrice * 10000
      : mtgSavePreReqTemp.housePrice,

    disposalPlanYn:
      mtgSavePreReqTemp.disposalPlanYn === ''
        ? null
        : mtgSavePreReqTemp.disposalPlanYn,
    previousYn:
      mtgSavePreReqTemp.previousYn === '' ? null : mtgSavePreReqTemp.previousYn,
    tenantYn:
      mtgSavePreReqTemp.tenantYn === '' ? null : mtgSavePreReqTemp.tenantYn,
    firstYn:
      mtgSavePreReqTemp.firstYn === '' ? null : mtgSavePreReqTemp.firstYn,
    openDt: mtgSavePreReqTemp.openDt === '' ? null : mtgSavePreReqTemp.openDt,
  });
  const saveInputInfo = async () => {
    return await putInputInfo();
  };

  return {
    progress: Number(location.pathname.slice(-1)) * (100 / 7),
    mappedCodeList,

    navigate,
    navigateNextStep,

    selectSheet,
    openSelectSheet,

    mortgageInfo,
    onChangeWrappingInput,
    onChangeInput,

    saveInputInfoTemp,
    saveInputInfo,
  };
};
